import {
  forwardRef,
  useState,
  useRef,
  useCallback,
  useEffect,
  type ChangeEvent,
  type KeyboardEvent,
  type SyntheticEvent,
  type UIEvent,
} from 'react';
import { getAtContext, insertAtMention } from '@/utils/atMention';
import { renderWithMentions } from '@/utils/renderMentions';
import { cn } from '@/utils/helpers';
import { AtMentionDropdown } from './AtMentionDropdown';

export type MentionFieldMode = 'entity' | 'template';

export type MentionInputElement = HTMLTextAreaElement | HTMLInputElement;

export interface MentionTextFieldProps {
  value: string;
  onChange: (value: string, cursorPos: number) => void;
  /** `entity` = pick a record (chat); `template` = @user / @device placeholders (saved tasks). */
  mentionMode: MentionFieldMode;
  variant?: 'composer' | 'modal';
  placeholder?: string;
  disabled?: boolean;
  /** Hide picker while another trigger (e.g. `/`) is active in the parent. */
  suppressMentionPicker?: boolean;
  /** One-line field (saved-task modal, home composer). */
  singleLine?: boolean;
  /** Focus the field on mount (uses ref + effect, not the native autoFocus attribute). */
  focusOnMount?: boolean;
  rows?: number;
  className?: string;
  'aria-label'?: string;
  onKeyDown?: (e: KeyboardEvent<MentionInputElement>) => void;
  onMentionPickerChange?: (open: boolean) => void;
}

/**
 * Shared @-mention field (overlay + popover). Composer uses textarea; modal uses single-line input.
 */
export const MentionTextField = forwardRef<MentionInputElement, MentionTextFieldProps>(
  function MentionTextField(
    {
      value,
      onChange,
      mentionMode,
      variant = 'composer',
      placeholder,
      disabled = false,
      suppressMentionPicker = false,
      singleLine = false,
      focusOnMount = false,
      rows = 1,
      className,
      'aria-label': ariaLabel,
      onKeyDown,
      onMentionPickerChange,
    },
    ref
  ) {
    const isSingleLine = variant === 'modal' || singleLine;
    const [showMention, setShowMention] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const pickerVisible = showMention && !disabled && !suppressMentionPicker;
    const inputRef = useRef<MentionInputElement | null>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    const setInputRef = useCallback(
      (el: MentionInputElement | null) => {
        inputRef.current = el;
        if (!isSingleLine) {
          if (typeof ref === 'function') ref(el as HTMLTextAreaElement | null);
          else if (ref) ref.current = el as HTMLTextAreaElement | null;
        }
      },
      [isSingleLine, ref]
    );

    const syncMention = useCallback(
      (val: string, cursorPos: number) => {
        const ctx = getAtContext(val, cursorPos);
        if (ctx) {
          setShowMention(true);
          setMentionQuery(ctx.query);
          onMentionPickerChange?.(true);
        } else {
          setShowMention(false);
          setMentionQuery('');
          onMentionPickerChange?.(false);
        }
      },
      [onMentionPickerChange]
    );

    useEffect(() => {
      if (!focusOnMount) return;
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }, [focusOnMount]);

    const resizeComposerTextarea = useCallback(() => {
      if (isSingleLine) return;
      const el = inputRef.current;
      if (!el || el.tagName !== 'TEXTAREA') return;
      const textarea = el as HTMLTextAreaElement;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }, [isSingleLine]);

    useEffect(() => {
      resizeComposerTextarea();
    }, [value, resizeComposerTextarea]);

    const normalizeValue = (raw: string) => (isSingleLine ? raw.replace(/\r?\n/g, ' ') : raw);

    const syncOverlayScroll = () => {
      const el = inputRef.current;
      const overlay = overlayRef.current;
      if (!el || !overlay || !isSingleLine) return;
      overlay.scrollLeft = el.scrollLeft;
    };

    const handleChange = (e: ChangeEvent<MentionInputElement>) => {
      const val = normalizeValue(e.target.value);
      const cursorPos = e.target.selectionStart ?? val.length;
      onChange(val, cursorPos);
      syncMention(val, cursorPos);
      requestAnimationFrame(() => {
        syncOverlayScroll();
        resizeComposerTextarea();
      });
    };

    const handleSelect = (e: SyntheticEvent<MentionInputElement>) => {
      const el = e.currentTarget;
      syncMention(el.value, el.selectionStart ?? el.value.length);
    };

    const handleScroll = (e: UIEvent<HTMLTextAreaElement>) => {
      const overlay = overlayRef.current;
      if (!overlay) return;
      overlay.scrollTop = e.currentTarget.scrollTop;
      overlay.scrollLeft = e.currentTarget.scrollLeft;
    };

    const handleKeyDown = (e: KeyboardEvent<MentionInputElement>) => {
      if (e.key === 'Escape' && pickerVisible) {
        e.preventDefault();
        e.stopPropagation();
        setShowMention(false);
        setMentionQuery('');
        onMentionPickerChange?.(false);
        return;
      }
      if (isSingleLine && e.key === 'Enter') {
        e.preventDefault();
        onKeyDown?.(e);
        return;
      }
      onKeyDown?.(e);
    };

    const handleMentionSelect = (token: string) => {
      const el = inputRef.current;
      const cursorPos = el?.selectionStart ?? value.length;
      const { value: next, cursorPos: nextPos } = insertAtMention(value, cursorPos, token);
      onChange(next, nextPos);
      setShowMention(false);
      setMentionQuery('');
      onMentionPickerChange?.(false);
      requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(nextPos, nextPos);
      });
    };

    const sharedInputProps = {
      className: cn('mention-field__input', isSingleLine && 'mention-field__input--single'),
      value,
      onChange: handleChange,
      onSelect: handleSelect,
      onKeyUp: handleSelect,
      onClick: handleSelect,
      onKeyDown: handleKeyDown,
      placeholder,
      disabled,
      spellCheck: false as const,
      'aria-label': ariaLabel,
    };

    return (
      <div
        className={cn(
          'mention-field',
          variant === 'modal' && 'mention-field--modal',
          isSingleLine && variant === 'composer' && 'mention-field--single-line',
          !isSingleLine && variant === 'composer' && 'mention-field--multiline-composer',
          pickerVisible && 'mention-field--picker-open',
          disabled && 'mention-field--disabled',
          className
        )}
      >
        <div className="mention-field__wrap">
          <div ref={overlayRef} className="mention-field__overlay" aria-hidden="true">
            {value ? renderWithMentions(value) : null}
          </div>
          {isSingleLine ? (
            <input ref={setInputRef} type="text" {...sharedInputProps} />
          ) : (
            <textarea ref={setInputRef} {...sharedInputProps} onScroll={handleScroll} rows={rows} />
          )}
          {pickerVisible ? (
            <AtMentionDropdown
              mode={mentionMode}
              placement="popover"
              query={mentionQuery}
              onSelect={handleMentionSelect}
              onClose={() => {
                setShowMention(false);
                setMentionQuery('');
                onMentionPickerChange?.(false);
              }}
              className="mention-field__mention"
            />
          ) : null}
        </div>
      </div>
    );
  }
);

MentionTextField.displayName = 'MentionTextField';
